import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Loader2, ShieldCheck, Save, Gauge, ScrollText, AlertOctagon, Check } from "lucide-react";

const inputCls = "bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-cobalt";

// Module-scope so number inputs keep focus while typing.
const Num = ({ k, label, cfg, setCfg, min = 0, max = 1000 }) => (
  <label className="block">
    <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">{label}</span>
    <input type="number" inputMode="numeric" min={min} max={max} value={cfg[k]} data-testid={`vcfg-${k}`}
      onChange={(e) => setCfg((c) => ({ ...c, [k]: Number(e.target.value) }))} className={`${inputCls} mt-1 w-full`} />
  </label>
);

// Super Admin verification controls: config, weights, blacklists, holds & overrides.
export default function AdminVerification() {
  const [cfg, setCfg] = useState(null);
  const [holds, setHolds] = useState([]);
  const [audit, setAudit] = useState([]);
  const [dash, setDash] = useState(null);
  const [reason, setReason] = useState("");
  const [cfgReason, setCfgReason] = useState("");
  const [busy, setBusy] = useState(null);

  const load = () => {
    api.get("/admin/verification-config").then(({ data }) => setCfg(data)).catch(() => {});
    api.get("/admin/verification/holds").then(({ data }) => setHolds(data)).catch(() => {});
    api.get("/admin/verification/audit").then(({ data }) => setAudit(data)).catch(() => {});
    api.get("/admin/verification-dashboard").then(({ data }) => setDash(data)).catch(() => {});
  };
  useEffect(load, []);

  const saveCfg = async () => {
    setBusy("save");
    try {
      const { data } = await api.put("/admin/verification-config", { ...cfg, reason: cfgReason || undefined });
      setCfg(data);
      toast.success("Verification settings saved");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed"); }
    finally { setBusy(null); }
  };

  const override = async (caseId, field, value) => {
    if (reason.trim().length < 3) { toast.error("Enter an override reason first (recorded permanently)."); return; }
    setBusy(caseId + field);
    try {
      await api.post(`/admin/cases/${caseId}/verification-override`, { field, value, reason: reason.trim() });
      toast.success(`Override applied: ${field}`);
      setReason(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Override failed"); }
    finally { setBusy(null); }
  };

  if (!cfg) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-cobalt animate-spin" /></div>;

  return (
    <div className="space-y-5" data-testid="admin-verification">
      {dash && (
        <div className="bg-white rounded-3xl border border-slate-100 p-6" data-testid="verification-dashboard">
          <h3 className="font-heading font-semibold text-navy flex items-center gap-2"><Gauge className="w-5 h-5 text-cobalt" /> Verification Pipeline</h3>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-3">
            {[["temporary_cases", "Temporary cases"], ["awaiting_documents", "Awaiting documents"],
              ["awaiting_rn_review", "Awaiting RN review"], ["needs_more_information", "Needs more info"],
              ["fraud_hold", "Fraud / hold"], ["rn_verified", "RN verified"], ["drone_approved", "Drone approved"],
              ["marketplace_eligible", "Marketplace eligible"], ["rejected", "Rejected"],
              ["expiring_temp_evidence", "Expiring temp evidence"]].map(([k, label]) => (
              <div key={k} className="rounded-2xl bg-slate-50 px-4 py-3" data-testid={`dash-${k}`}>
                <div className="font-heading text-2xl font-extrabold text-navy">{dash.pipeline?.[k] ?? 0}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">{label}</div>
              </div>
            ))}
          </div>
          <div className="mt-4 grid sm:grid-cols-4 gap-3 text-xs">
            <div className="rounded-2xl border border-slate-100 p-3" data-testid="dash-storage">
              <div className="font-semibold text-navy mb-1">Storage usage</div>
              {Object.entries(dash.storage || {}).map(([k, v]) => (
                <div key={k} className="flex justify-between text-slate-500"><span>{k}</span><span>{v.count} · {(v.bytes / 1048576).toFixed(1)} MB</span></div>
              ))}
            </div>
            <div className="rounded-2xl border border-slate-100 p-3" data-testid="dash-ai">
              <div className="font-semibold text-navy mb-1">AI processing</div>
              <div className="flex justify-between text-slate-500"><span>OCR processed</span><span>{dash.ai_usage?.ocr_processed ?? 0}</span></div>
              <div className="flex justify-between text-slate-500"><span>Uploads screened</span><span>{dash.ai_usage?.screened_uploads ?? 0}</span></div>
            </div>
            <div className="rounded-2xl border border-slate-100 p-3" data-testid="dash-rn">
              <div className="font-semibold text-navy mb-1">RN utilization</div>
              <div className="flex justify-between text-slate-500"><span>Active nurses</span><span>{dash.rn_utilization?.active_nurses ?? 0}</span></div>
              <div className="flex justify-between text-slate-500"><span>In progress</span><span>{dash.rn_utilization?.intakes_in_progress ?? 0}</span></div>
              <div className="flex justify-between text-slate-500"><span>Completed</span><span>{dash.rn_utilization?.intakes_completed ?? 0}</span></div>
            </div>
            <div className="rounded-2xl border border-slate-100 p-3" data-testid="dash-drone">
              <div className="font-semibold text-navy mb-1">Drone & marketplace</div>
              {Object.entries(dash.drone_utilization || {}).map(([k, v]) => (
                <div key={k} className="flex justify-between text-slate-500"><span>{k.replace(/_/g, " ")}</span><span>{v}</span></div>
              ))}
              <div className="flex justify-between text-slate-600 font-medium mt-1 pt-1 border-t border-slate-100">
                <span>Marketplace conversion</span>
                <span>{dash.marketplace_conversion?.claimed ?? 0}/{dash.marketplace_conversion?.listed ?? 0} ({dash.marketplace_conversion?.rate ?? 0}%)</span>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-3xl border border-slate-100 p-6">
        <h3 className="font-heading font-semibold text-navy flex items-center gap-2"><Gauge className="w-5 h-5 text-cobalt" /> Verification Settings</h3>
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-6 gap-4">
          <Num k="temp_retention_hours" label="Temp retention (hrs)" min={1} max={720} cfg={cfg} setCfg={setCfg} />
          <Num k="max_upload_mb" label="Max upload (MB)" min={1} max={500} cfg={cfg} setCfg={setCfg} />
          <Num k="min_rn_score" label="Min RN score" max={100} cfg={cfg} setCfg={setCfg} />
          <Num k="min_marketplace_score" label="Min marketplace score" max={100} cfg={cfg} setCfg={setCfg} />
          <Num k="min_permanent_score" label="Min permanent score" max={100} cfg={cfg} setCfg={setCfg} />
          <Num k="min_drone_score" label="Min drone score" max={100} cfg={cfg} setCfg={setCfg} />
        </div>
        <div className="mt-4 grid sm:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Drone coverage states (comma-separated, empty = all)</span>
            <input value={(cfg.drone_coverage_states || []).join(", ")} data-testid="vcfg-drone-coverage"
              onChange={(e) => setCfg({ ...cfg, drone_coverage_states: e.target.value.split(",").map((x) => x.trim().toUpperCase()).filter(Boolean) })}
              className={`${inputCls} mt-1 w-full`} placeholder="TX, FL, NC" />
          </label>
          <div className="flex items-end gap-4 flex-wrap pb-1">
            {Object.entries(cfg.feature_flags || {}).map(([k, v]) => (
              <label key={k} className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={!!v} data-testid={`vcfg-flag-${k}`}
                  onChange={(e) => setCfg({ ...cfg, feature_flags: { ...cfg.feature_flags, [k]: e.target.checked } })}
                  className="accent-cobalt h-4 w-4" />
                {k.replace(/_/g, " ")}
              </label>
            ))}
          </div>
        </div>
        <label className="mt-4 flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={cfg.require_live_capture} data-testid="vcfg-live-capture"
            onChange={(e) => setCfg({ ...cfg, require_live_capture: e.target.checked })} className="accent-cobalt h-4 w-4" />
          Require live camera capture for initial verification
        </label>
        <div className="mt-5">
          <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Trust Score weights (points per factor)</span>
          <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {Object.entries(cfg.trust_weights).map(([k, v]) => (
              <label key={k} className="flex items-center justify-between gap-2 rounded-xl bg-slate-50 px-3 py-2">
                <span className="text-xs text-slate-600 capitalize">{k.replace(/_/g, " ")}</span>
                <input type="number" min={0} max={100} value={v} data-testid={`vcfg-weight-${k}`}
                  onChange={(e) => setCfg({ ...cfg, trust_weights: { ...cfg.trust_weights, [k]: Number(e.target.value) } })}
                  className="w-16 bg-white border border-slate-200 rounded-lg px-2 py-1 text-sm outline-none focus:border-cobalt" />
              </label>
            ))}
          </div>
        </div>
        <div className="mt-5 grid sm:grid-cols-4 gap-4">
          {[["phone_blacklist", "Phone blacklist"], ["device_blacklist", "Device blacklist"], ["email_blacklist", "Email blacklist"], ["ip_blacklist", "IP blacklist"]].map(([k, label]) => (
            <label key={k} className="block">
              <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">{label} (comma-separated)</span>
              <textarea value={(cfg[k] || []).join(", ")} data-testid={`vcfg-${k}`}
                onChange={(e) => setCfg({ ...cfg, [k]: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })}
                className={`${inputCls} mt-1 w-full min-h-[60px]`} />
            </label>
          ))}
        </div>
        <div className="mt-5 flex flex-col sm:flex-row gap-3 sm:items-end">
          <label className="block flex-1">
            <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Change reason (recorded in the immutable audit log)</span>
            <input value={cfgReason} onChange={(e) => setCfgReason(e.target.value)} data-testid="vcfg-reason"
              placeholder="Why are you changing these settings?" className={`${inputCls} mt-1 w-full`} />
          </label>
          <button onClick={saveCfg} disabled={busy === "save"} data-testid="vcfg-save"
            className="flex items-center gap-2 px-6 py-3 rounded-full bg-cobalt text-white text-sm font-semibold hover:bg-cobalt-hover transition-all disabled:opacity-50">
            <Save className="w-4 h-4" /> {busy === "save" ? "Saving…" : "Save settings"}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-slate-100 p-6">
        <h3 className="font-heading font-semibold text-navy flex items-center gap-2"><AlertOctagon className="w-5 h-5 text-amber-500" /> Holds & Manual Reviews</h3>
        <label className="mt-3 block">
          <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Override reason (required — recorded in the immutable audit log)</span>
          <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="override-reason"
            placeholder="e.g. Verified documents manually with the client" className={`${inputCls} mt-1 w-full`} />
        </label>
        <div className="mt-4 space-y-3">
          {holds.length === 0 ? <p className="text-sm text-slate-400">No cases on hold or pending manual review.</p> :
            holds.map((c) => {
              const v = c.verification || {};
              return (
                <div key={c.case_id} className="rounded-2xl border border-slate-100 p-4" data-testid={`hold-case-${c.case_id}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div>
                      <span className="font-semibold text-navy text-sm">{c.case_number || c.case_id}</span>
                      <span className="ml-2 text-xs text-slate-400">Trust {v.trust_score ?? "—"} · {v.trust_class || ""}</span>
                    </div>
                    <div className="flex gap-1.5 flex-wrap">
                      {v.on_hold && <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-red-50 text-red-600">On hold</span>}
                      {v.gps_manual_review?.status === "pending" && <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-amber-50 text-amber-700">GPS manual review</span>}
                    </div>
                  </div>
                  {(v.hold_reasons || []).length > 0 && <div className="mt-1 text-xs text-slate-500">{v.hold_reasons.join(" · ")}</div>}
                  {v.gps_manual_review?.reason && <div className="mt-1 text-xs text-slate-500">GPS reason: “{v.gps_manual_review.reason}”</div>}
                  <div className="mt-3 flex gap-2 flex-wrap">
                    {v.on_hold && <OverrideBtn label="Release hold" onClick={() => override(c.case_id, "release_hold")} busy={busy === c.case_id + "release_hold"} testid={`release-hold-${c.case_id}`} />}
                    {v.gps_manual_review?.status === "pending" && <OverrideBtn label="Approve GPS manually" onClick={() => override(c.case_id, "gps_manual_approved")} busy={busy === c.case_id + "gps_manual_approved"} testid={`approve-gps-${c.case_id}`} />}
                    <OverrideBtn label="Mark ID verified" onClick={() => override(c.case_id, "driver_id_status", "verified")} busy={busy === c.case_id + "driver_id_status"} testid={`verify-id-${c.case_id}`} />
                    <OverrideBtn label="Mark insurance verified" onClick={() => override(c.case_id, "insurance_status", "verified")} busy={busy === c.case_id + "insurance_status"} testid={`verify-ins-${c.case_id}`} />
                    <OverrideBtn label="Promote evidence to permanent" onClick={() => override(c.case_id, "promote_evidence")} busy={busy === c.case_id + "promote_evidence"} testid={`promote-ev-${c.case_id}`} />
                  </div>
                </div>
              );
            })}
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-slate-100 p-6">
        <h3 className="font-heading font-semibold text-navy flex items-center gap-2"><ScrollText className="w-5 h-5 text-cobalt" /> Verification Override Audit (immutable)</h3>
        <div className="mt-3 space-y-2" data-testid="verification-audit-list">
          {audit.length === 0 ? <p className="text-sm text-slate-400">No overrides recorded.</p> :
            audit.map((e) => (
              <div key={e.audit_id} className="rounded-xl bg-slate-50 px-4 py-3 text-sm">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="font-medium text-navy">{e.field}: {String(e.original)} → {String(e.new)}</span>
                  <span className="text-xs text-slate-400">{new Date(e.occurred_at).toLocaleString()}</span>
                </div>
                <div className="text-xs text-slate-500 mt-0.5">“{e.reason}” — {e.admin_name || e.admin_email}{e.case_id ? ` · case ${e.case_id}` : " · config change"}</div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

const OverrideBtn = ({ label, onClick, busy, testid }) => (
  <button onClick={onClick} disabled={busy} data-testid={testid}
    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-xs font-semibold text-slate-600 hover:border-cobalt hover:text-cobalt transition-all disabled:opacity-50">
    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} {label}
  </button>
);
